import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Divider,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import React, { useMemo, useRef, useState } from 'react';
import { isPrometheeResourceGroupName } from '../../../utils/prometheeGroups';
import { useCreateUser } from '../../../utils/api/admin/useCreateUser';
import { useDeleteUser } from '../../../utils/api/admin/useDeleteUser';
import { useGetAvailableUsers } from '../../../utils/api/admin/useGetAvailableUsers';
import { useGetGroups } from '../../../utils/api/admin/useGetGroups';
import { useGetUsers } from '../../../utils/api/admin/useGetUsers';
import { useUpdateUser } from '../../../utils/api/admin/useUpdateUser';
import PermissionMatrix from './PermissionMatrix';

const GLOBAL_GROUP_NAMES = [
  'Promethee:Read',
  'Promethee:Update',
  'Promethee:Admin',
];

/**
 * Wrapper pour brancher PermissionMatrix sur un <Form.Item /> (value/onChange injectés).
 */
const PermissionMatrixField = ({
  value,
  onChange,
  allGroups,
  coveredByGlobalRole,
}) => (
  <PermissionMatrix
    allGroups={allGroups}
    selectedGroupIds={value ?? []}
    onChange={onChange}
    coveredByGlobalRole={coveredByGlobalRole}
  />
);

const UsersList = () => {
  const [drawer, setDrawer] = useState({ open: false, user: null });
  const [search, setSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [form] = Form.useForm();

  const isEditing = !!drawer.user;

  const { users, isLoading } = useGetUsers({ search });
  const { data: groupsData } = useGetGroups();
  const allGroups = useMemo(
    () => groupsData?.results ?? groupsData ?? [],
    [groupsData]
  );

  // Utilisateurs BO existants éligibles (hors Promethee) — uniquement en création.
  const { data: availableUsers = [], isFetching: availableLoading } =
    useGetAvailableUsers(userSearch, {
      enabled: drawer.open && !isEditing,
    });

  // Debounce de la recherche pour ne pas requêter à chaque frappe.
  const searchTimer = useRef();
  const handleUserSearch = (value) => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setUserSearch(value), 300);
  };

  // Split global vs par-ressource pour les deux widgets du formulaire.
  const { globalGroups, resourceGroups } = useMemo(() => {
    const global = allGroups.filter((g) => GLOBAL_GROUP_NAMES.includes(g.name));
    const resource = allGroups.filter((g) =>
      isPrometheeResourceGroupName(g.name)
    );
    return { globalGroups: global, resourceGroups: resource };
  }, [allGroups]);

  const closeDrawer = () => {
    setDrawer({ open: false, user: null });
    setUserSearch('');
    form.resetFields();
  };

  const { createUser, isPending: creating } = useCreateUser({
    onSuccess: () => {
      message.success('Accès Prométhée accordé');
      closeDrawer();
    },
    onError: (e) =>
      message.error(
        e.response?.data?.email ?? e.response?.data?.detail ?? 'Erreur'
      ),
  });
  const { updateUser, isPending: updating } = useUpdateUser({
    onSuccess: () => {
      message.success('Permissions mises à jour');
      closeDrawer();
    },
    onError: (e) => message.error(e.response?.data?.detail ?? 'Erreur'),
  });
  const { deleteUser } = useDeleteUser({
    onSuccess: () => message.success('Accès Prométhée retiré'),
    onError: (e) => message.error(e.response?.data?.detail ?? 'Erreur'),
  });

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({ global_group_ids: [], resource_group_ids: [] });
    setDrawer({ open: true, user: null });
  };

  const openEdit = (user) => {
    const userGroupNames = new Set(user.groups ?? []);
    const globalIds = globalGroups
      .filter((g) => userGroupNames.has(g.name))
      .map((g) => g.id);
    const resourceIds = resourceGroups
      .filter((g) => userGroupNames.has(g.name))
      .map((g) => g.id);
    form.setFieldsValue({
      email: user.email,
      global_group_ids: globalIds,
      resource_group_ids: resourceIds,
    });
    setDrawer({ open: true, user });
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      const group_ids = [
        ...(values.global_group_ids ?? []),
        ...(values.resource_group_ids ?? []),
      ];
      if (isEditing) {
        await updateUser({
          id: drawer.user.id,
          body: { group_ids },
        });
      } else {
        await createUser({ email: values.email, group_ids });
      }
    } catch {
      // Validation errors and mutation failures are handled by Form/React Query callbacks.
    }
  };

  // Griser les cases de la matrice déjà couvertes par un rôle global coché.
  const selectedGlobalIds = Form.useWatch('global_group_ids', form) ?? [];
  const selectedGlobalNames = new Set(
    globalGroups
      .filter((g) => selectedGlobalIds.includes(g.id))
      .map((g) => g.name)
  );
  let coveredByGlobalRole = null;
  if (
    selectedGlobalNames.has('Promethee:Admin') ||
    selectedGlobalNames.has('Promethee:Update')
  ) {
    coveredByGlobalRole = 'Update';
  } else if (selectedGlobalNames.has('Promethee:Read')) {
    coveredByGlobalRole = 'Read';
  }

  const columns = [
    { title: 'Email', dataIndex: 'email' },
    { title: 'Prénom', dataIndex: 'first_name' },
    { title: 'Nom', dataIndex: 'last_name' },
    {
      title: 'Groupes Prométhée',
      dataIndex: 'groups',
      render: (groups = []) => {
        const globals = groups.filter((g) => GLOBAL_GROUP_NAMES.includes(g));
        const fineCount = groups.length - globals.length;
        return (
          <Space size={4} wrap>
            {globals.map((g) => (
              <Tag key={g} color="purple">
                {g}
              </Tag>
            ))}
            {fineCount > 0 && <Tag>{fineCount} perm. fines</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Actif (BO)',
      dataIndex: 'is_active',
      render: (v) => (v ? <Tag color="green">Oui</Tag> : <Tag>Non</Tag>),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, user) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(user)}
          />
          <Popconfirm
            title="Retirer l'accès Prométhée à cet utilisateur ?"
            description="Le compte BO reste intact."
            okText="Retirer"
            cancelText="Annuler"
            onConfirm={() => deleteUser(user.id)}
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Rechercher (email, nom)"
          onSearch={setSearch}
          allowClear
          style={{ width: 280 }}
        />
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Ajouter un utilisateur
        </Button>
      </Space>

      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={users}
        pagination={{ pageSize: 20 }}
      />

      <Drawer
        title={
          isEditing
            ? `Modifier les droits — ${drawer.user.email}`
            : 'Ajouter un utilisateur à Prométhée'
        }
        open={drawer.open}
        onClose={closeDrawer}
        width={780}
        extra={
          <Button
            type="primary"
            loading={creating || updating}
            onClick={submit}
          >
            Enregistrer
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          {isEditing ? (
            <Form.Item name="email" label="Utilisateur">
              <Input disabled />
            </Form.Item>
          ) : (
            <Form.Item
              name="email"
              label="Utilisateur BO existant"
              rules={[
                { required: true, message: 'Sélectionnez un utilisateur BO' },
              ]}
              extra="Seuls les comptes déjà présents dans le back-office peuvent recevoir un accès Prométhée."
            >
              <Select
                showSearch
                filterOption={false}
                onSearch={handleUserSearch}
                loading={availableLoading}
                placeholder="Rechercher par nom, email ou identifiant..."
                notFoundContent={
                  availableLoading
                    ? 'Recherche...'
                    : userSearch
                      ? 'Aucun utilisateur BO éligible'
                      : 'Tapez pour rechercher'
                }
                options={availableUsers.map((u) => {
                  const fullName =
                    `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim();
                  return {
                    value: u.email,
                    label: `${fullName || u.username} — ${u.email}`,
                  };
                })}
              />
            </Form.Item>
          )}

          <Divider orientation="left">Rôles globaux</Divider>
          <Typography.Paragraph type="secondary">
            Raccourci pour couvrir toutes les ressources en une fois.
            <br />
            <code>Promethee:Read</code> = lecture seule ·{' '}
            <code>Promethee:Update</code> = Créer/Lire/Modifier (pas Supprimer)
            · <code>Promethee:Admin</code> = tout + gestion des utilisateurs.
          </Typography.Paragraph>
          <Form.Item name="global_group_ids">
            <Select
              mode="multiple"
              allowClear
              placeholder="Aucun rôle global"
              options={globalGroups.map((g) => ({
                label: g.name,
                value: g.id,
              }))}
            />
          </Form.Item>

          <Divider orientation="left">Permissions par ressource</Divider>
          <Form.Item name="resource_group_ids" noStyle>
            <PermissionMatrixField
              allGroups={resourceGroups}
              coveredByGlobalRole={coveredByGlobalRole}
            />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default UsersList;
